/**
 * ISO 8601 duration parsing.
 *
 * Matches `P[n]Y[n]M[n]DT[n]H[n]M[n]S` — e.g. `PT1H`, `PT30M`, `P1D`,
 * `P1DT12H`, `PT1H30M15S`. The seconds component allows a single decimal
 * fraction (e.g. `PT1.5S`).
 *
 * Year and month conversions to seconds are calendar approximations
 * (`30 days` and `365 days` respectively). Sufficient for comparing two
 * user-entered retry intervals; not for date arithmetic.
 */

/**
 * Regex for ISO 8601 durations. Exported so callers that prefer to drive
 * their own validation can reuse the canonical pattern.
 */
export const ISO_8601_DURATION_PATTERN =
  /^P(?:(\d+)Y)?(?:(\d+)M)?(?:(\d+)D)?(?:T(?:(\d+)H)?(?:(\d+)M)?(?:(\d+(?:\.\d+)?)S)?)?$/i;

export interface IsoDurationParts {
  years: number;
  months: number;
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
}

/**
 * Parse an ISO 8601 duration string into its individual components.
 *
 * Returns `null` for nullish input or malformed strings. Empty strings
 * are NOT treated as valid here (use `isValidIsoDuration` for the
 * "optional field" check).
 */
export function parseIsoDuration(value: string | undefined | null): IsoDurationParts | null {
  if (!value) return null;
  const trimmed = value.trim().toUpperCase();
  const match = ISO_8601_DURATION_PATTERN.exec(trimmed);
  if (!match) return null;

  return {
    years: parseInt(match[1] ?? '0', 10),
    months: parseInt(match[2] ?? '0', 10),
    days: parseInt(match[3] ?? '0', 10),
    hours: parseInt(match[4] ?? '0', 10),
    minutes: parseInt(match[5] ?? '0', 10),
    seconds: parseFloat(match[6] ?? '0'),
  };
}

/**
 * Convert an ISO 8601 duration to total seconds.
 *
 * Year = 365 days, month = 30 days. Returns `null` for malformed input.
 */
export function parseIsoDurationToSeconds(value: string | undefined | null): number | null {
  const parts = parseIsoDuration(value);
  if (!parts) return null;

  return (
    parts.years * 365 * 24 * 3600 +
    parts.months * 30 * 24 * 3600 +
    parts.days * 24 * 3600 +
    parts.hours * 3600 +
    parts.minutes * 60 +
    parts.seconds
  );
}

/**
 * Check whether a string is a valid ISO 8601 duration.
 *
 * Nullish or empty (after trim) values are considered valid — the
 * caller-side semantics are "optional field". Use `parseIsoDuration` if
 * you need actual numeric components.
 */
export function isValidIsoDuration(value: string | undefined | null): boolean {
  if (value === undefined || value === null) return true;
  const trimmed = value.trim();
  if (trimmed === '') return true;
  const upper = trimmed.toUpperCase();
  return upper.startsWith('P') && ISO_8601_DURATION_PATTERN.test(upper);
}
